import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  MultiselectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useCreateMatterTaskPlan,
  useDeleteMatterTaskPlan,
  useUpdateMatterTaskPlan,
} from "@/lib/data/react-query/matters/matter-task-plan";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterTaskPlan } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const MATTER_TASK_PLAN_FORM_ID = "matter-task-plan-editor-form";

const matterTaskPlanModalFieldsetClassName =
  "relative grid grid-cols-1 gap-4 sm:grid-cols-2";

interface AddOrEditMatterTaskPlanModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onTransitionToEdit?: (plan: MatterTaskPlan) => void;
}

interface ConditionalProps {
  editing?: boolean;
  activeMatterTaskPlanId?: string;
  isReadOnly?: boolean;
}

const formSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, {
      message: "Title is required",
    })
    .max(100, { message: "Title must be 100 characters or less" }),
  description: z
    .string()
    .max(5000, { message: "Description must be 5,000 characters or less" })
    .nullable()
    .optional(),
  incidentTypeIds: z.array(z.string()).min(1, {
    message: "Incident type is required",
  }),
});

type FormValues = z.infer<typeof formSchema>;

const defaultFormValues = {
  title: "",
  description: "",
  incidentTypeIds: [],
};

export const AddOrEditMatterTaskPlanModal = ({
  open,
  setOpen,
  editing,
  activeMatterTaskPlanId,
  onTransitionToEdit,
  isReadOnly,
}: AddOrEditMatterTaskPlanModalProps & ConditionalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const activeMatterTaskPlan = matterSettingsData?.data.taskPlans.find(
    (plan) => plan.id === activeMatterTaskPlanId,
  );
  const defaultEditingValues = useMemo(
    () => ({
      ...defaultFormValues,
      ...activeMatterTaskPlan,
      description: activeMatterTaskPlan?.description ?? "",
      incidentTypeIds: activeMatterTaskPlan?.incidentTypeIds ?? [],
    }),
    [activeMatterTaskPlan],
  );
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: editing ? defaultEditingValues : defaultFormValues,
    mode: "onSubmit",
  });

  const incidentTypes = matterSettingsData?.data.incidentTypes ?? [];

  const updateMatterTaskPlan = useUpdateMatterTaskPlan();
  const createMatterTaskPlan = useCreateMatterTaskPlan();
  const deleteMatterTaskPlan = useDeleteMatterTaskPlan();

  const isFormLoading =
    updateMatterTaskPlan.isPending ||
    createMatterTaskPlan.isPending ||
    deleteMatterTaskPlan.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (editing) {
      if (!activeMatterTaskPlan) return;
      if (updateMatterTaskPlan.isPending) {
        return;
      }
      const { data: updatedPlan } = await updateMatterTaskPlan.mutateAsync({
        id: activeMatterTaskPlan.id,
        ...data,
        dateModified: activeMatterTaskPlan.dateModified,
      });
      if (closeAfterSave) {
        setOpen(false);
      } else {
        form.reset({
          ...defaultFormValues,
          ...updatedPlan,
          incidentTypeIds: updatedPlan.incidentTypeIds ?? [],
        });
      }
    } else {
      if (createMatterTaskPlan.isPending) {
        return;
      }
      const { data: createdPlan } = await createMatterTaskPlan.mutateAsync({
        ...data,
      });
      if (closeAfterSave) {
        setOpen(false);
      } else {
        onTransitionToEdit?.(createdPlan);
        form.reset({
          ...defaultFormValues,
          ...createdPlan,
          incidentTypeIds: createdPlan.incidentTypeIds ?? [],
        });
      }
    }
    setCloseAfterSave(false);
  };

  const handleDelete = async () => {
    if (!activeMatterTaskPlan) {
      return;
    }
    setIsDeleting(false);
    setOpen(false);
    await deleteMatterTaskPlan.mutateAsync({
      id: activeMatterTaskPlan.id,
    });
  };

  useEffect(() => {
    if (!open) {
      if (editing && activeMatterTaskPlan) {
        form.reset(defaultEditingValues);
      } else {
        form.reset(defaultFormValues);
      }
      setCloseAfterSave(false);
    }
  }, [open, form, activeMatterTaskPlan, editing, defaultEditingValues]);

  const modalTitle = editing ? "Edit task plan" : "Create task plan";
  const modalDescription = editing
    ? "Update the task plan details below."
    : "Create a new task plan for your firm.";

  const showDeleteInFooter =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTaskPlan,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = showDeleteInFooter && (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  );

  const cancelAndSubmitFooter = (
    <div className="flex grow justify-end gap-x-2">
      <FormCancelButton
        label={isReadOnly ? "Close" : "Cancel"}
        setOpen={setOpen}
        isDirty={form.formState.isDirty}
      />
      {!isReadOnly && (
        <>
          <FormSubmitButton
            submitFormId={MATTER_TASK_PLAN_FORM_ID}
            variant="light-primary"
            loading={
              updateMatterTaskPlan.isPending || createMatterTaskPlan.isPending
            }
            disabled={isFormLoading}
            submitFn={() => {
              setCloseAfterSave(false);
            }}
          />
          <FormSubmitButton
            submitFormId={MATTER_TASK_PLAN_FORM_ID}
            loading={
              updateMatterTaskPlan.isPending || createMatterTaskPlan.isPending
            }
            disabled={isFormLoading}
            submitFn={() => {
              setCloseAfterSave(true);
            }}
            title="Save and close"
          />
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Form wraps AppModal so footer controls (FormSubmitButton useFormContext) are inside the provider */}
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={modalTitle}
          description={modalDescription}
          size="2xl"
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              {deleteButton}
              {cancelAndSubmitFooter}
            </div>
          }
        >
          <form
            id={MATTER_TASK_PLAN_FORM_ID}
            className="relative"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <fieldset
              disabled={isReadOnly}
              className={matterTaskPlanModalFieldsetClassName}
            >
              <div className="col-span-2">
                <TextFormField
                  label="Title*"
                  placeholder="Enter task plan title"
                  register={form.register("title")}
                  dataTestId="matter-task-plan-title-input"
                />
              </div>
              <MultiselectFormField
                label="Incident types*"
                options={incidentTypes.map((incidentType) => ({
                  label: getIncidentTypeLabel(incidentType),
                  value: incidentType.id,
                }))}
                register={form.register("incidentTypeIds")}
              />
              <div className="col-span-2">
                <TextAreaFormField
                  label="Description"
                  register={form.register("description")}
                />
              </div>
            </fieldset>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this task plan?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
